import type { BriefDraft, Format } from "./brief.js";
import type { SendingGate } from "./mailbox.js";
import type { Operation } from "./provider.js";
import type { Motion as ShotMotion, ShotType } from "./script.js";
import type { Scene } from "./scene.js";

/**
 * What each endpoint answers with (docs/17 §architecture, [ADR 0016](../../../docs/adr/0016-a-response-has-one-description.md)).
 *
 * `contracts` already held the domain — a brief, a script, money, an id — and both sides imported it. What it did
 * not hold was the *shape of an answer*: every route assembled one inline with `c.json({…})` and the browser
 * declared its own idea of the same payload in `apps/web/src/api.ts`. Two descriptions of one truth, with nothing
 * between them, in a product where the single-page app ships separately from the API and a tab can be running last
 * week's bundle. Overbill lost an evening to exactly that on 2026-09-08.
 *
 * Two rules make these types worth the file they are in:
 *
 * 1. **They describe the wire, not the row.** Every timestamp is an ISO string, because that is what arrives in the
 *    browser. The route is what does the converting, so the type is the same object on both sides rather than two
 *    that happen to serialize alike.
 * 2. **They are exact.** Every route's view function declares one of these as its return type, so an added field
 *    that nobody wrote down is a build failure at the route, and a removed field is a build failure at the screen.
 */

/** Every failure the API can answer with. `ref` is the number the user reads down the phone (16). */
export interface ErrorResponse {
  error: string;
  ref?: string;
  /** Only on a 400 from a schema: zod's own issues, passed through for the browser to show. */
  issues?: unknown;
}

/* ── media ───────────────────────────────────────────────────────────────────────────────────────────────────── */

export type MediaKind = "image" | "video" | "audio" | "caption" | "font";
/**
 * Where a file came from. `website`, `google` and `web` are pictures a lookup found and the owner chose to keep: off
 * their own site, off their own Google listing, and off pages about them elsewhere (ADR 0029).
 */
export type MediaOrigin = "uploaded" | "generated" | "captured" | "library" | "website" | "google" | "web";

/** One row of the media library. `url` is this API's own route, never a provider's and never a bucket's. */
export interface MediaView {
  id: string;
  kind: MediaKind;
  origin: MediaOrigin;
  mime: string;
  bytes: number;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  caption: string | null;
  tags: string[];
  hasPeople: boolean | null;
  url: string;
  /**
   * A small copy, about 480 px wide, for anything drawn smaller than the picture: a tile, a thumbnail, a video's poster
   * frame (2026-09-25). Always a working address: until the copy is made it answers with the original.
   */
  thumbUrl: string;
  createdAt: string;
}

/** The sized copies kept beside an original (`media.variants`). Empty once tried and not possible. */
export interface MediaVariants {
  thumb?: { key: string; mime: string; width: number; height: number; bytes: number };
}

/** `GET /v1/media` */
export type MediaListResponse = MediaView[];
/** `POST /v1/media` — 201, or the same row again when those exact bytes are already stored. */
export type MediaUploadResponse = MediaView;

/**
 * `GET /v1/media/search?q=` — pictures found for you rather than owned by you.
 *
 * `available` is the honest field: no stock provider is connected yet (docs/14), and a screen that offered a
 * search box which silently returned nothing would read as a broken product rather than an unbuilt one.
 */
export interface MediaSearchResponse {
  available: boolean;
  results: MediaView[];
  note?: string;
}

/* ── workspace and spend ─────────────────────────────────────────────────────────────────────────────────────── */

export type CallStatus = "queued" | "running" | "parked" | "succeeded" | "failed";

/** One provider call, priced. `chargedUsd` is what was taken; `reservedUsd` is what is only being held (docs/13). */
export interface CallView {
  id: string;
  projectId: string | null;
  shotId: string | null;
  operation: Operation;
  provider: string;
  model: string;
  status: CallStatus;
  chargedUsd: string | null;
  reservedUsd: string | null;
  durationMs: number | null;
  error: string | null;
  createdAt: string;
  /** What it read and what it wrote, for the calls that are measured that way. Null for the ones that are not. */
  inputTokens: number | null;
  outputTokens: number | null;
  /** How the price was arrived at — tokens, seconds, requests. The units a bill would itemise. */
  units: Array<{ kind: string; count: number }> | null;
  /** Which workspace spent it, in the person's list across all of theirs (`/v1/me/spend`, ADR 0021). */
  workspaceName?: string | null;
}

/** `GET /v1/workspace` */
export interface WorkspaceResponse {
  /** Who answers in the chat box, as `provider:model` (`chat.ts`). */
  chatModel: string;
  id: string;
  name: string;
  /** Its address in words: this tab's screens are `/w/<slug>/<tab>` (ADR 0052). */
  slug: string;
  /** The balance and daily limit of the account this workspace spends (ADR 0021) — the same in all of a person's workspaces. */
  balanceUsd: string;
  dailyCapUsd: string;
  /**
   * The month against the cap, where one is set, so the plan can say "about $0.85 — this month: $1.41 of $2.00"
   * before the button rather than after it (ADR 0012 point 6). The same numbers as `/v1/me/credits`; they are here
   * too because this is the one call every screen already makes.
   */
  monthUsd: string;
  monthlyCapUsd: string | null;
  business: { name: string; industry: string | null; palette: string[] | null } | null;
  /**
   * Whether this person would get through the desk's gate right now (docs/18): an operator, signed in freshly enough,
   * with the desk switched on. The header shows the Desk link on it, as Overbill's does. False for everybody else, so
   * a customer's page learns nothing it could not learn by typing /desk.
   */
  desk: boolean;
}

/**
 * The header a tab sends with the id of the workspace it is drawing (ADR 0006 amendment). Two tabs share one session,
 * so a switch in one re-points the other; the API answers a mismatch with 409 and `code: WORKSPACE_CHANGED` before
 * any route runs, and the tab reloads rather than writing into a business it is not showing.
 */
export const WORKSPACE_HEADER = "x-workspace";
/**
 * The same, as a query parameter, for what a browser loads without a header: a picture, a video, a download, the
 * progress stream (dev box, 2026-09-25 — every one of them ran in the session's default workspace and came back 404).
 * The API reads it exactly as it reads the header, through membership.
 */
export const WORKSPACE_PARAM = "w";
/** An address that names its workspace, for a browser to load with no header. */
export const inWorkspaceUrl = (url: string, workspaceId: string) => `${url}${url.includes("?") ? "&" : "?"}${WORKSPACE_PARAM}=${encodeURIComponent(workspaceId)}`;
/** One of a workspace's files, as the browser loads it (ADR 0051), naming the workspace it belongs to. */
export const mediaFileUrl = (id: string, workspaceId: string | null, size?: "thumb") => {
  const url = `/v1/media/${id}/file${size ? `?size=${size}` : ""}`;
  return workspaceId ? inWorkspaceUrl(url, workspaceId) : url;
};
export const WORKSPACE_CHANGED = "workspace_changed";

/**
 * What every workspace route answers, with a 403, to a person with no open workspace — all of theirs closed (ADR 0022).
 * The app draws the page that offers a new one. `/v1/me`, `/v1/workspaces` and the desk still answer them.
 */
export const NO_WORKSPACE = "no_workspace";

/** One workspace in the switcher. */
export interface WorkspaceSummary {
  id: string;
  name: string;
  /** Its address in words (`/w/<slug>`, ADR 0052). */
  slug: string;
  /** The one this session is in. Exactly one row has it. */
  active: boolean;
}

/** `GET /v1/workspaces` — every workspace this person is in, and whether they may make another. */
export interface WorkspacesResponse {
  workspaces: WorkspaceSummary[];
  /** How many open workspaces this person has made, sign-up's included, and the most they may. A closed one stops counting (ADR 0022). */
  created: number;
  limit: number;
}

/** `POST /v1/workspaces` — made, and this session is now in it. */
export interface CreateWorkspaceResponse {
  id: string;
}

/** `POST /v1/workspaces/:id/name`, carrying `{ name }` — renamed, and the name as it was written down. */
export interface RenameWorkspaceResponse {
  id: string;
  name: string;
}

/** `POST /v1/workspaces/:id/close`, carrying `{ name }` — closed, and when what was in it is deleted (ADR 0022). */
export interface CloseWorkspaceResponse {
  purgeAfter: string;
}

/**
 * One page of a list the server holds whole (2026-09-23): the rows, and enough to say "11–20 of 143" and whether
 * there is a next one. Pages count from 0 and are `PAGE_SIZE` long, so a screen never has to be told how long.
 */
export interface Page<T> {
  rows: T[];
  page: number;
  total: number;
}
export const PAGE_SIZE = 10;
/** Which calls the Credits list is showing: everything, the ones still under way, or the ones that failed. */
export type CallFilter = "all" | "running" | "failed";

/**
 * `GET /v1/me/spend?page=&only=` — every call across the person's workspaces, each saying where it was spent
 * (ADR 0021), a page at a time and filtered on the server, so "failed" finds a failure from last spring too.
 */
export interface SpendResponse {
  totalUsd: string;
  calls: Page<CallView>;
}

/** One purchase of credit, and what is left of it. Lots drain oldest first and expire a year after they were bought. */
export interface CreditLot {
  id: string;
  purchasedUsd: string;
  remainingUsd: string;
  purchasedAt: string;
  expiresAt: string;
  status: "active" | "exhausted" | "expired" | "refunded";
}

/** Every movement of money, in the order it happened. A spend row points at the call that caused it. */
export interface LedgerEntry {
  id: string;
  kind: "purchase" | "spend" | "expiry" | "refund" | "adjustment";
  deltaUsd: string;
  note: string | null;
  providerCallId: string | null;
  createdAt: string;
  /** Where a spend happened. Null for money arriving, which is spent nowhere. */
  workspaceName?: string | null;
}

/**
 * Where a person stands against their own two ceilings (guards 3 and 4,
 * [13](../../../docs/13-cost-guards.md), [ADR 0012](../../../docs/adr/0012-pay-as-you-go-with-a-monthly-cap.md)).
 *
 * Both caps and both running totals travel together, because every screen that shows one shows the other: a cap with
 * no total beside it is a number nobody can act on, and a total with no cap is a number that means nothing.
 * `monthlyCapUsd` is null when the person has set none, which is the default and is not the same as zero.
 */
export interface Limits {
  dayUsd: string;
  dailyCapUsd: string;
  monthUsd: string;
  monthlyCapUsd: string | null;
}

/** `GET /v1/me/credits` — the person's credit, the same whichever workspace is open (ADR 0021). */
export interface CreditsResponse {
  balanceUsd: string;
  /** What a lot expiring soon is worth, so the screen can say it before it goes. */
  expiringUsd: string;
  /**
   * The first page of each, so opening the panel is one read; the rest are `GET /v1/me/credits/lots?page=` and
   * `/ledger?page=`. `lots.total === 0` is how a screen knows nothing has ever been bought.
   */
  lots: Page<CreditLot>;
  ledger: Page<LedgerEntry>;
  limits: Limits;
  /**
   * Whether credit can be bought on this box at all.
   *
   * False on a laptop and on any box without the card processor's keys, and the top-up packs then say so rather
   * than failing when pressed (ADR 0028: there is no pretend payment). The packs travel with it so the API and the
   * screen cannot disagree about what is for sale.
   */
  cardPayments: boolean;
  packs: number[];
  /** Whether Stripe's billing page — invoices, receipts, billing details — can be opened: they have bought here (34). */
  billingPage?: boolean;
}

/** `POST /v1/me/credits/billing` — a short-lived link to the person's own billing page at Stripe (34). */
export interface BillingPageResponse {
  url: string;
}

/** `PATCH /v1/me/limits`, carrying dollars — and `monthlyUsd: null` to take the monthly cap off again. */
export interface SetLimits {
  dailyUsd?: string;
  monthlyUsd?: string | null;
}

/** `POST /v1/me/credits/top-up`, carrying `{ dollars }` — where to send the browser. Always the processor's domain. */
export interface TopUpResponse {
  url: string;
}

export type AlertKind =
  | "global_cap" | "workspace_cap" | "workspace_monthly_cap" | "project_budget"
  | "attempts_exhausted" | "deadline_expired" | "paused_refusal" | "insufficient_credits" | "workspace_closed";

/**
 * A guard's refusal in the customer's words (ADR 0016: one description, and it lives here).
 *
 * Until now nothing turned these into a sentence, so a step the guards stopped simply did not happen and the Studio
 * said nothing at all — the gap [14](../../../docs/14-open-items.md) listed as "a refused step shows no message".
 *
 * Two of the nine are the customer's own doing and read as a normal day; the rest are ours, and say what we know
 * without pretending it was their fault. None of them blames the person, and none of them mentions a provider by
 * name: which vendor was short of capacity is not a fact a small business can act on.
 */
export function alertWords(kind: AlertKind): string {
  switch (kind) {
    case "workspace_monthly_cap":
      return "This month's limit was reached, so nothing more will run until you raise it or the month turns.";
    case "workspace_cap":
      return "Your daily limit was reached. Nothing more runs today unless you raise it.";
    case "project_budget":
      return "This post reached the cap on what one post may spend, and stopped with what was made so far.";
    case "insufficient_credits":
      return "There was not enough credit left to pay for the next step.";
    case "global_cap":
      return "Joogoos stopped its own spending for the day. This is ours, not yours, and we have been told.";
    case "paused_refusal":
      return "This step is switched off at our end for the moment. We have been told.";
    case "attempts_exhausted":
      return "This step was tried three times and did not work. Running it again is the way forward.";
    case "deadline_expired":
      return "This step waited too long for an answer and was stopped. Nothing was charged for it.";
    case "workspace_closed":
      return "This workspace is closed, so nothing in it can spend.";
  }
}

/** Whether a refusal is the customer's own ceiling doing its job, rather than something of ours going wrong. */
export const isOwnCeiling = (kind: AlertKind): boolean => kind === "workspace_cap" || kind === "workspace_monthly_cap";

/** One thing a guard refused, for the owner rather than for the customer (docs/13). */
export interface AlertView {
  id: string;
  kind: AlertKind;
  projectId: string | null;
  callId: string | null;
  detail: unknown;
  createdAt: string;
  acknowledgedAt: string | null;
}

/** `GET /v1/workspace/alerts` */
export type AlertsResponse = AlertView[];

/* ── take everything with you (ADR 0019) ─────────────────────────────────────────────────────────────────────────── */

/** `expired` is also what a finished export past its date reads as, before the sweep has got to it. */
export type ExportStatus = "queued" | "running" | "ready" | "failed" | "expired";

/** One zip of an export. `url` is this API's own route, which checks the session before it hands over a signed link. */
export interface ExportPartView {
  n: number;
  bytes: number;
  files: number;
  url: string;
}

/** One export as the Workspace panel draws it. `parts` is empty until it is ready, and empty again once it has ended. */
export interface ExportView {
  id: string;
  status: ExportStatus;
  createdAt: string;
  finishedAt: string | null;
  expiresAt: string | null;
  projects: number;
  videos: number;
  photos: number;
  bytes: number;
  parts: ExportPartView[];
}

/** `GET /v1/workspace/exports`: the latest few, newest first. */
export interface ExportsResponse {
  exports: ExportView[];
}

/* ── projects ────────────────────────────────────────────────────────────────────────────────────────────────── */

export type { ShotMotion, ShotType };

export type ProjectKind = "post" | "video";
export type ProjectStatus =
  | "chatting" | "scripted" | "generating" | "over_budget" | "failed" | "rendered" | "published" | "archived";
export type MessageRole = "user" | "assistant" | "system" | "tool";
export type ShotStatus = "planned" | "generating" | "ready" | "failed";
export type AssetKind = "image" | "video" | "voice" | "music" | "captions";
export type RenderKind = "preview" | "final";
export type RenderStatus = "queued" | "running" | "succeeded" | "failed";

/**
 * How long a post's name may be, wherever it came from: cut from the idea, or typed over it (docs/03, 2026-09-21).
 * Long enough to say what the post is, short enough that the rail's 220 px column still shows most of it.
 */
export const PROJECT_TITLE_MAX = 60;

/**
 * A row in the rail. Deliberately thinner than the project itself.
 *
 * `needs` is what turns the rail from a list of everything into a list of what is waiting: null for a project that
 * is simply finished, and otherwise the reason in the user's words — a draft left unfinished, a render that failed,
 * a project stopped at its cap. A column that only lists is a column you stop reading.
 */
export interface ProjectSummary {
  id: string;
  title: string;
  status: ProjectStatus;
  kind: ProjectKind | null;
  updatedAt: string;
  needs: string | null;
  /** Whether the rail offers to delete it: still a draft, never generated, no photos chosen (`projectDeletable`). */
  deletable: boolean;
  /** The day it is planned for, when it came from Prepare my week (`week.ts`); null for everything else. */
  planFor: string | null;
}

/** `GET /v1/projects` */
export type ProjectListResponse = ProjectSummary[];

/** A day. After this, a conversation nobody came back to is a conversation that was abandoned. */
const STALE = 24 * 60 * 60 * 1000;

/**
 * Why a project wants you, or null if it does not.
 *
 * Deliberately narrow: only states the customer can do something about. A project that is generating is not
 * waiting for anyone, and a finished one is finished — putting either in the worklist would teach people to
 * ignore it, which is the failure mode of every list like this.
 */
export function projectNeeds(status: ProjectStatus, updatedAt: string | Date): string | null {
  const at = typeof updatedAt === "string" ? Date.parse(updatedAt) : updatedAt.getTime();
  if (status === "over_budget") return "stopped at its cap";
  if (status === "chatting" && Date.now() - at > STALE) return "left unfinished";
  return null;
}

/**
 * Whether a project is still nothing: a chat with nothing answered in it. New post opens this one rather than making
 * another, so the rail never fills with empty drafts (docs/03, 2026-09-12).
 */
export function projectEmpty(p: { status: ProjectStatus; draft: unknown }): boolean {
  return p.status === "chatting" && Object.keys((p.draft ?? {}) as object).length === 0;
}

/**
 * Whether a project can be deleted: never generated, so still in the chat, and no photos chosen for it. Nothing that
 * was made can be lost this way. What its chat turns were charged stays in Credits (docs/03, 2026-09-12).
 *
 * A planned piece of the week can go whatever photos it names (`week.ts`): Joogoos chose them, not the owner, and
 * dropping a day of the plan is the one thing somebody reading a week has to be able to do.
 */
export function projectDeletable(p: { status: ProjectStatus; draft: unknown; planFor?: Date | string | null }): boolean {
  const draft = (p.draft ?? {}) as { sourceMediaIds?: unknown[] };
  return p.status === "chatting" && (!draft.sourceMediaIds?.length || (p.planFor ?? null) !== null);
}

export interface MessageView {
  id: string;
  role: MessageRole;
  /**
   * Free text, and what the message did to the brief: what the owner's words or button set (`patch`) or took away
   * (`clear`), or what the guide suggested (`proposed`).
   */
  content: { text?: string; patch?: unknown; clear?: string[]; proposed?: unknown };
  createdAt: string;
}

export interface ScriptView {
  version: number;
  premise: string;
  tone: string;
  audience: string;
  coreMessage: string;
  durationMs: number;
  /**
   * Whether it was written for a Showreel video, by its version's frozen brief: the draft in the chat may have changed
   * style since. In one, a narrated piece's voice says its lines, so the Studio prices a change of words from this, as
   * the server decides it (review, 2026-09-28).
   */
  showreel: boolean;
}

/** The selected asset of each kind, by kind. Absent means nothing has been made for that kind yet. */
export type ShotAssets = Partial<Record<AssetKind, { version: number; media: MediaView }>>;

export interface ShotView {
  id: string;
  position: number;
  type: ShotType;
  description: string;
  durationMs: number;
  dialogue: string | null;
  /** Whether its line is the voice's: a shot speaks, and a rerun can redo its voice, only with a line and this. */
  isNarration: boolean;
  onScreenText: string | null;
  motion: ShotMotion;
  status: ShotStatus;
  sourceMediaId: string | null;
  /** A drawn scene in place of a picture (ADR 0050); null for a photo shot. */
  scene: Scene | null;
  assets: ShotAssets;
}

export interface RenderView {
  id: string;
  kind: RenderKind;
  status: RenderStatus;
  error: string | null;
  durationMs: number | null;
  createdAt: string;
  output: MediaView | null;
  /**
   * Where each shot sits in this cut. The cut is longer than the script's plan: a spoken shot runs until its words are
   * said and breathes after them, and the last one holds a moment (docs/30), so the editor lays its timeline out from
   * these and not from the planned lengths. Empty when the composition is not known.
   */
  shots: Array<{ id: string; startMs: number; durationMs: number }>;
}

/**
 * The whole project: one snapshot, and the only thing the event stream ever sends.
 *
 * It is deliberately everything — the chat, the script, the shots, the renders and the calls — because a browser
 * that has this needs nothing else to draw any stage of the Studio. That is what lets the stream cost one request
 * rather than four (docs/17 §performance 3).
 */
export interface ProjectView {
  id: string;
  title: string;
  status: ProjectStatus;
  kind: ProjectKind | null;
  format: Format | null;
  budgetUsd: string | null;
  spentUsd: string;
  draft: BriefDraft;
  /** The day it is planned for, when it came from Prepare my week; the publish dialog starts there. */
  planFor: string | null;
  /** Fields of the brief still to answer, in the order the chat asks them. */
  missing: string[];
  /** Fields of the brief that are the Studio guide's suggestions rather than the owner's decisions (ADR 0031). */
  proposed: string[];
  nextQuestion: { field: string; text: string } | null;
  messages: MessageView[];
  script: ScriptView | null;
  shots: ShotView[];
  /** Newest first: the first is what the post is at, and a later one is an earlier try. */
  renders: RenderView[];
  calls: CallView[];
}

/** `GET /v1/projects/:id`, and what every write to a project answers with. */
export type ProjectResponse = ProjectView;

/* ── the rest ────────────────────────────────────────────────────────────────────────────────────────────────── */

/** `GET /v1/auth/config` — what the sign-in screen may offer. */
export interface AuthConfigResponse {
  google: boolean;
  /** Whether `/review` can sign anybody in: the app reviewer's password login is configured on this box (33). */
  review?: boolean;
}

/**
 * `GET /health`. Also what the account menu reads for its foot line, `Version 0.7.2 · a1b2c3d` (docs/03), so the build
 * it names is the one answering rather than a number typed into the web app.
 */
export interface HealthResponse {
  ok: boolean;
  service: string;
  /** `BUILD_VERSION`: the release the image was built as; for a ship, the last release before it; `0.0.0-dev` on a laptop (docs/09). */
  version: string;
  /** `BUILD_SHA`: the full commit, or `dev` when nobody passed one, which is a laptop or an image built by hand. */
  sha: string;
}

/**
 * The line at the foot of the account menu: the version, and the short commit when the build knows it. A build given no
 * `BUILD_SHA` — a laptop, an image built by hand — says `Version 0.0.0-dev` and nothing after it rather than `· dev`.
 */
export function buildLabel({ version, sha }: Pick<HealthResponse, "version" | "sha">): string {
  const commit = (sha ?? "").trim();
  const short = commit === "" || commit === "dev" || commit === "unknown" ? "" : commit.slice(0, 7);
  return short ? `Version ${version} · ${short}` : `Version ${version}`;
}

/* ── the desk (18) ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * `GET /v1/desk/spend` — panel 1 of the 9 in docs/18.
 *
 * Every figure is micro-dollars as a string (ADR 0002), the same as everywhere else: money never becomes a float
 * on the way to a screen. The panel exists so a cap is a backstop rather than the first warning — `spentMicro`
 * against `capMicro` is the number nobody could see before, and `hours` is the slope under it.
 */
export interface DeskSpendResponse {
  /** Today so far: what customers were charged for their own calls. */
  spentMicro: string;
  reservedMicro: string;
  /** What those customer calls cost at the providers today. The margin is `spentMicro` minus this, and nothing else. */
  customerCostMicro: string;
  /** What the app paid providers today for calls it charges to nobody (ADR 0025), and how many there were. */
  appCostMicro: string;
  appCalls: number;
  /** Today as the global cap counts it: customers at what they were charged, the app at cost, calls in flight at their reservation. */
  countedMicro: string;
  capMicro: string;
  /** Where the Spend panel raises its flag, as a percentage of the cap. */
  warnPct: number;
  calls: number;
  failed: number;
  /** True when any operation is paused: guard 1 is on, and the product is quietly doing nothing. */
  paused: boolean;
  pausedOperations: string[];
  /** Charged, cost and margin per payer, provider, model and operation, today. */
  byModel: DeskSpendRow[];
  /** What the app paid for, by purpose: today and across the window. Empty until the app makes a call of its own. */
  byPurpose: DeskAppPurposeRow[];
  /** The last `days` UTC days including today, split by who pays: a month's shape rather than a day's. */
  window: { days: number; calls: number; customerChargedMicro: string; customerCostMicro: string; appCalls: number; appCostMicro: string };
  /** Spend per UTC hour today, oldest first: the slope a cap cannot show. */
  hours: { hour: string; chargedMicro: string; appCostMicro: string; calls: number }[];
  /** The workspaces spending it, largest first. Counts and money, never content. The app's own calls are in `appCalls`. */
  workspaces: { workspaceId: string; name: string; chargedMicro: string; calls: number; balanceMicro: string }[];
}

export interface DeskAppPurposeRow {
  purpose: string;
  callsToday: number;
  costTodayMicro: string;
  /** What those calls read and wrote today, as the providers counted it: the figures `costTodayMicro` was priced from. */
  tokensInToday: number;
  tokensOutToday: number;
  /** Across the window in `DeskSpendResponse.window`. */
  calls: number;
  failed: number;
  costMicro: string;
}

export interface DeskSpendRow {
  /** Who pays (ADR 0025). An app row is charged nothing, so it has a cost and no margin. */
  payer: "customer" | "app";
  provider: string;
  model: string;
  operation: string;
  calls: number;
  failed: number;
  chargedMicro: string;
  costMicro: string;
  paused: boolean;
}

/** One guard refusal (13). `detail` is whatever the guard recorded — a cap, a budget, an attempt count. */
export interface DeskAlertView {
  id: string;
  /** `AlertKind`, not a second copy of the list: one description of one thing (ADR 0016). */
  kind: AlertKind;
  workspaceId: string | null;
  workspaceName: string | null;
  projectId: string | null;
  callId: string | null;
  detail: Record<string, unknown> | null;
  createdAt: string;
  /** Null until a person says they have seen it. Acknowledging is not fixing. */
  acknowledgedAt: string | null;
}

/**
 * `GET /v1/desk/alerts` — panel 2 of the 10 in docs/18.
 *
 * The `alerts` table has had an `acknowledged_at` column since the day it was written and nothing has ever set it.
 * This is the reader, and `POST /v1/desk/alerts/:id/acknowledge` is the writer.
 */
export interface DeskAlertsResponse {
  days: number;
  total: number;
  open: number;
  /** How long the oldest unacknowledged one has been sitting there. The number that says nobody is looking. */
  oldestOpenAt: string | null;
  byKind: { kind: DeskAlertView["kind"]; total: number; open: number; lastAt: string }[];
  alerts: DeskAlertView[];
}

/** `GET /v1/desk/models` — what each operation is routed to, and guard 1's pause state (13). */
export interface DeskModelsResponse {
  paused: number;
  operations: {
    operation: string; provider: string; model: string; paused: boolean;
    updatedAt: string; updatedBy: string | null;
    /** Today, so a pause can be read against what it is actually stopping. */
    callsToday: number; chargedMicroToday: string; failedToday: number;
  }[];
  flags: string[];
}

/** `GET /v1/desk/queue` — pg-boss, which is the queue (ADR 0003). */
export interface DeskQueueResponse {
  available: boolean;
  flags: string[];
  queues: { name: string; state: string; count: number; oldest: string | null }[];
  failed: { id: string; name: string; retryCount: number; retryLimit: number; completedOn: string | null; output: string | null }[];
  schedules: { name: string; cron: string; timezone: string | null }[];
}

/** `GET /v1/desk/renders` — where a render fails, and where its minutes go. */
export interface DeskRendersResponse {
  windowHours: number;
  flags: string[];
  outcomes: { status: string; count: number }[];
  steps: { step: string; medianMs: number | null; p90Ms: number | null; slowestMs: number | null; count: number }[];
  failures: { id: string; projectId: string; kind: string; durationMs: number | null; createdAt: string; error: string | null }[];
}

/** `GET /v1/desk/release` — is what is deployed what is answering? */
export interface DeskReleaseResponse {
  api: { version: string };
  latest: { sha: string; version: string; subject: string | null; startedAt: string; notifiedAt: string | null } | null;
  running: { sha: string; requests: number; firstSeen: string; lastSeen: string }[];
  flags: string[];
}

/** `GET /v1/desk/requests` — what is failing, what is slow, and what is that reference. */
export interface DeskRequestsResponse {
  windowHours: number;
  health: { rows: number; oldest: string | null; serverErrors: number; refusals: number };
  prunedAt: string | null;
  failures: { path: string; status: number; count: number; lastAt: string; sampleReference: string | null }[];
  slowest: { path: string; count: number; medianMs: number | null; p90Ms: number | null; slowestMs: number | null }[];
  found: DeskLogRow[];
}

export interface DeskLogRow {
  at: string; kind: string; method: string | null; path: string; status: number | null;
  durationMs: number | null; workspaceId: string | null; actor: string | null;
  reference: string | null; buildSha: string | null;
}

/** `GET /v1/desk/logs` — the rows themselves, filtered. `pnpm logs` with its flags as a form. */
export interface DeskLogsResponse {
  windowHours: number;
  kinds: string[];
  filter: { kind: string | null; status: number | null; slower: number | null; path: string | null; reference: string | null; limit: number };
  health: { rows: number; oldest: string | null; newest: string | null };
  prunedAt: string | null;
  byKind: { kind: string; count: number }[];
  truncated: boolean;
  rows: DeskLogRow[];
}

/** `GET /v1/desk/workspaces` — who is here, what they are worth, and when they last did anything. */
export interface DeskWorkspacesResponse {
  total: number;
  flags: string[];
  workspaces: {
    id: string; name: string; slug: string; createdAt: string;
    /** Whose account it spends, and that account's balance and daily cap (ADR 0021). */
    payer: string; members: number; balanceMicro: string; dailyCapMicro: string;
    projects: number; renders: number;
    spentMicro: string; calls: number;
    lastRequestAt: string | null; lastCallAt: string | null;
  }[];
}

/** `GET /v1/desk/credits` — does the money add up? */
export interface DeskCreditsResponse {
  flags: string[];
  /** An account whose ledger and balance disagree is a bug in the ledger, and nothing else would show it (ADR 0021). */
  drift: { userId: string; name: string; balanceMicro: string; ledgerMicro: string; driftMicro: string }[];
  expiring: { id: string; userId: string; name: string; remainingMicro: string; expiresAt: string; status: string }[];
  byKind: { kind: string; count: number; totalMicro: string }[];
  /** A settled call with no ledger row is money spent that nobody was charged for. */
  unledgered: number;
  /** Every account, so the one panel that can move money can also find who to move it to (ADR 0048). */
  accounts: { userId: string; name: string; email: string; balanceMicro: string; lots: number }[];
  /** The latest card purchases, each with its Stripe payment to open, and what was refunded on it (34). */
  purchases?: {
    id: string; name: string; purchasedMicro: string; remainingMicro: string; refundedMicro: string;
    purchasedAt: string; status: string; paymentId: string; stripeUrl: string;
  }[];
}

/**
 * `POST /v1/desk/credits` — the operator puts credit on an account (ADR 0048).
 *
 * The other way in besides a card, and the only one that works on a box nobody holds a shell on ([ADR 0020](../../../docs/adr/0020-a-dev-box-and-a-live-box-and-nobody-holds-a-shell-on-live.md)).
 * It is a grant and not a bypass: the money arrives as a lot, on the same account, spent through the same guards and
 * counted by the same ledger as a purchase. There is nobody who spends without credit.
 */
export interface DeskGrantCredit {
  /** Whose account. An address rather than an id, because an address is what the operator has. */
  email: string;
  /** Dollars as a string, between `GRANT_MIN_USD` and `GRANT_MAX_USD`. */
  dollars: string;
  /** Why, in a few words. It goes on the ledger row and into `audit_log`, and it is what makes a grant readable later. */
  note?: string;
}

/** What the grant did, so the panel can say it rather than reload and hope. */
export interface DeskGrantResponse {
  email: string;
  addedUsd: string;
  balanceUsd: string;
}

/**
 * `GET /v1/desk/mail` — how much of the month's sending is left.
 *
 * The panel that did not exist on 2026-09-20, when a provider's free hundred ran out and every message for two days
 * was accepted and binned. The counts are this product's own (`mail_sent`), not the provider's: a provider that
 * lies about having sent is exactly the case this has to survive.
 */
export interface DeskMailResponse {
  flags: string[];
  month: number;
  day: number;
  monthLimit: number;
  dayLimit: number;
  /** A refusal is not counted against the allowance — it was not taken — and is the first thing worth reading. */
  refused: { at: string; kind: string; error: string | null }[];
  byKind: { kind: string; sent: number }[];
}

/**
 * `GET /v1/desk/customers` — is Customers working, anywhere?
 *
 * **Counts and never companies.** Every field below is a number, a code, or a sentence this product wrote about
 * itself. A prospect's name, its address, and the words one business wrote to it are not readable by the desk's
 * role at all (migration 0030), so this shape is not a promise about what the panel chooses to draw — it is the
 * only shape the grants allow.
 */
export interface DeskCustomersRow {
  workspaceId: string;
  workspaceName: string | null;
  found: number;
  go: number;
  look: number;
  no: number;
  letters: number;
  replies: number;
  meetings: number;
  bounced: number;
  optedOut: number;
  noReply: number;
  suppressed: number;
  /** The workspace's own sending switch, and whether a mailbox is actually connected behind it. */
  sendingOn: boolean;
  mailbox: "none" | "connected" | "paused" | "disconnected";
  /** The loop: off, drafting, or drafting and sending. */
  loop: "off" | "drafts" | "sends";
  /** Charged to this workspace for Customers this calendar month, in dollars. */
  spentThisMonth: string;
  /** Open pauses: cells that stopped themselves and are waiting for a person. */
  pauses: number;
  /** Proposals nobody has decided. */
  waiting: number;
  lastSentAt: string | null;
}

/** Why a letter did not go, counted. This product's own words, never a recipient's. */
export interface DeskRefusalRow {
  says: string;
  count: number;
}

export interface DeskCustomersResponse {
  /** What this box would let out at all, which is the first thing to check when nothing is sending anywhere. */
  gate: SendingGate;
  /** Whether this box can reach a mail provider and seal a token — without both, no box sends anything. */
  canSend: boolean;
  workspaces: DeskCustomersRow[];
  /** Across every workspace, why follow-ups were held back. The sharpest signal that something is wrong. */
  refusals: DeskRefusalRow[];
  /** A mailbox the provider has disconnected is a customer whose outreach has silently stopped. */
  brokenMailboxes: number;
  /** Cells that stopped themselves, across every workspace, waiting for their owner. */
  pausedCells: number;
}

/** `GET /v1/desk/limits` — why was somebody refused? A subject is a hash, so this is shape and never who. */
export interface DeskLimitsResponse {
  windowHours: number;
  buckets: { bucket: string; permits: number; windowSeconds: number }[];
  refusals: { path: string; count: number; lastAt: string; sampleReference: string | null }[];
  counted: { bucket: string; subject: string; windowStart: string; hits: number; ceiling: number | null; refusing: boolean }[];
}
